/**
 * useCancelSubscription + useResumeSubscription — in-app cancel & resume.
 *
 * in-app-checkout Phase 3 Session 3.5.
 *
 * **Cancel**: `DELETE /api/billing/subscription` → `subscriptions.update({
 *   cancel_at_period_end: true })`. Access continues until `period_end` then
 *   lapses (mirrors the Customer Portal `at_period_end` policy).
 *
 * **Resume**: `PUT /api/billing/subscription/resume` → `subscriptions.update({
 *   cancel_at_period_end: false })`. Clears the scheduled cancel — back to ACTIVE.
 *
 * On success both mutations invalidate `queryKeys.billing.usage(userId)` so the
 * hub refreshes with the updated `cancel_at` / badge state immediately.
 *
 * `retry: 0` on both — subscription mutations must not auto-retry (idempotency).
 *
 * Design note: `useCancelSubscription` and `useResumeSubscription` are two
 * separate hooks (not a single hook with a `mode` param) because they call
 * different HTTP verbs/paths and have independent pending/error state. The
 * BillingHub renders exactly one at a time (cancel vs resume button) so there
 * is no UI collision.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { SubscriptionMutationResponse } from '@arcanum-hq/shared-types';

interface CancelResumeEnvelope {
  data: SubscriptionMutationResponse;
}

/**
 * Cancel the subscription at period end (`at_period_end` policy — access
 * continues until `period_end`, no immediate cut-off, resumable).
 */
export function useCancelSubscription(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionMutationResponse, Error, void>({
    mutationFn: async () => {
      const envelope = await api.delete<CancelResumeEnvelope>(
        '/api/billing/subscription'
      );
      return envelope.data;
    },
    retry: 0,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.billing.usage(userId),
      });
    },
  });
}

/**
 * Resume a subscription scheduled for cancellation — clears `cancel_at_period_end`
 * so the subscription continues past the current period end.
 */
export function useResumeSubscription(userId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<SubscriptionMutationResponse, Error, void>({
    mutationFn: async () => {
      const envelope = await api.put<CancelResumeEnvelope>(
        '/api/billing/subscription/resume',
        {}
      );
      return envelope.data;
    },
    retry: 0,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.billing.usage(userId),
      });
    },
  });
}
